import { useState, createContext, ReactNode, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { auth } from '../../api/auth';

interface AuthContextType {
  login: (
    grant_type: any,
    tipoLogin: number,
    username: string,
    password: string,
  ) => Promise<{
    error: boolean;
    access_token?: string;
    CODIGO_USUARIO?: string;
    data?: string;
  }>;
  logout: () => void;
  token: string;
  CODIGO_USUARIO?: string;
  nomeUsuario?: string;
  isLoading: boolean;
}

export const AuthenticationContext = createContext<AuthContextType | undefined>(undefined);

interface Usuario {
  dscNome: string;
}

const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [token, setToken] = useState<string>('');
  const [CODIGO_USUARIO, setCODIGO_USUARIO] = useState<string | undefined>(undefined);
  const [nomeUsuario, setNomeUsuario] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const paramLogin =
      window.location.hostname === 'cms-dev.acto.net.br' ||
      window.location.hostname === 'cms-qa.acto.net.br' ||
      window.location.hostname === 'localhost'
        ? '6476'
        : '8720';
  
    api.defaults.headers['PARAM_LOGIN'] = paramLogin;
    api.defaults.headers['APP_ID'] = '23F49D8B-6885-4470-92D1-41A28C6F401C';
    api.defaults.headers['Content-Type'] = 'application/x-www-form-urlencoded';
  
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      setToken(storedToken);
      api.defaults.headers.Authorization = `Bearer ${storedToken}`;
    }
  
    const storedNomeUsuario = localStorage.getItem('dadosUsuario');
    if (storedNomeUsuario) {
      try {
        const usuario: Usuario = JSON.parse(storedNomeUsuario);
        setNomeUsuario(usuario.dscNome);
      } catch (error) {
        console.error('Erro com os dados do usuário:', error);
      }
    }
  
    setIsLoading(false);
  }, []);  

  const login = async (
    grant_type: any,
    tipoLogin: number,
    username: string,
    password: string
  ) => {
    try {
      const { data } = await auth(
        (grant_type = 'password'),
        (tipoLogin = 0),
        username,
        password
      );

      const { access_token, CODIGO_USUARIO, nomeUsuario } = data;

      localStorage.setItem('token', access_token);
      localStorage.setItem('CODIGO_USUARIO', CODIGO_USUARIO);
      localStorage.setItem('dadosUsuario', JSON.stringify({ dscNome: nomeUsuario }));

      api.defaults.headers.Authorization = `Bearer ${access_token}`;
      setToken(access_token);
      setCODIGO_USUARIO(CODIGO_USUARIO);
      setNomeUsuario(nomeUsuario);

      return {
        error: false,
        access_token,
        CODIGO_USUARIO
      };
    } catch (e: any) {
      console.error(e, 'error');
      if (e.response?.status === 404) {
        return { error: true, data: 'Usuário não encontrado' };
      } else if (e.response?.status === 401) {
        return { error: true, data: 'Senha incorreta' };
      } else {
        return { error: true, data: e.message };
      }
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('CODIGO_USUARIO');
    localStorage.removeItem('dadosUsuario');
    localStorage.removeItem('permissaoAcesso');
    setToken('');
    setCODIGO_USUARIO(undefined);
    setNomeUsuario(undefined);
    navigate('/login');
  };

  return (
    <AuthenticationContext.Provider
      value={{ login, logout, token, CODIGO_USUARIO, nomeUsuario, isLoading }}
    >
      {children}
    </AuthenticationContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthenticationContext);
  if (context === undefined) {
    throw new Error('O hook useAuth só pode ser utilizado dentro de um AuthProvider. Verifique se o componente está corretamente envolvido pelo provedor.');
  }
  return context;
};

export default AuthProvider;
